/**
 * Resolves motor FNOL driver capture mode for API submit from portal draft flags.
 * @module claims/motorFnolDriverSubmitFields
 */

import type { ClaimFlowDraft } from './claimDraftStorage'

export type MotorFnolDriverCaptureModeForSubmit = 'primary' | 'listed' | 'manual' | null

/**
 * Prefer who-was-driving flags and listed-driver selection over a stale `multiVehicleDriverCaptureMode`.
 */
export function resolveMotorFnolDriverCaptureModeForSubmit(
  d: ClaimFlowDraft
): MotorFnolDriverCaptureModeForSubmit {
  if (d.multiVehiclePrimaryDriverDriving === true) return 'primary'
  if (
    d.multiVehicleSecondaryOnPolicyDriving === true ||
    Boolean(d.multiVehicleListedDriverId?.trim())
  ) {
    return 'listed'
  }
  if (
    d.multiVehiclePrimaryDriverDriving === false &&
    d.multiVehicleSecondaryOnPolicyDriving === false
  ) {
    return 'manual'
  }
  const raw = d.multiVehicleDriverCaptureMode ?? null
  if (raw === 'primary' || raw === 'listed' || raw === 'manual') return raw
  return null
}

/** Driver-related POST fields shared by collision and fire FNOL submit APIs. */
export interface MotorFnolDriverSubmitFields {
  multiVehicleDriverCaptureMode: MotorFnolDriverCaptureModeForSubmit
  multiVehiclePrimaryDriverDriving: boolean | null
  multiVehicleSecondaryOnPolicyDriving: boolean | null
  multiVehicleListedDriverId: string | null
  driverFirstName: string | null
  driverLastName: string | null
  driverDobDdMmYyyy: string | null
  licenceNumber: string | null
  licenceExpiryDdMmYyyy: string | null
  yearsLicensed: number | null
}

export type MotorFnolDriverLicenceSlice = Pick<
  ClaimFlowDraft,
  | 'multiVehicleDriverDobDdMmYyyy'
  | 'multiVehicleLicenseNumber'
  | 'multiVehicleLicenseExpiryDdMmYyyy'
  | 'multiVehicleYearsLicensed'
>

/**
 * Builds driver POST fields for motor FNOL (collision, fire, etc.) from draft + licence slice.
 */
export function buildMotorFnolDriverSubmitFields(
  d: ClaimFlowDraft,
  driverLicence: MotorFnolDriverLicenceSlice | null
): MotorFnolDriverSubmitFields {
  return {
    multiVehicleDriverCaptureMode: resolveMotorFnolDriverCaptureModeForSubmit(d),
    multiVehiclePrimaryDriverDriving: d.multiVehiclePrimaryDriverDriving ?? null,
    multiVehicleSecondaryOnPolicyDriving: d.multiVehicleSecondaryOnPolicyDriving ?? null,
    multiVehicleListedDriverId: d.multiVehicleListedDriverId?.trim() || null,
    driverFirstName: d.multiVehicleDriverFirstName ?? null,
    driverLastName: d.multiVehicleDriverLastName ?? null,
    driverDobDdMmYyyy: driverLicence?.multiVehicleDriverDobDdMmYyyy ?? null,
    licenceNumber: driverLicence?.multiVehicleLicenseNumber ?? null,
    licenceExpiryDdMmYyyy: driverLicence?.multiVehicleLicenseExpiryDdMmYyyy ?? null,
    yearsLicensed: driverLicence?.multiVehicleYearsLicensed ?? null,
  }
}
